import { useEffect } from 'react';
import Button from '@/components/Button';
import CountrySelectBox, { CountryItem } from '@/components/CountrySelectBox';
import { BillingDetails } from '@/modules/billing/models/Billing';
import { Controller, useForm } from 'react-hook-form';

export type BillingDetailsFormData = {
  companyName: string;
  billingEmail: string;
  taxId: string;
  building: string;
  street1: string;
  street2: string;
  postalCode: string;
  region: string;
  city: string;
  countryCode: string;
};

const defaultValues: BillingDetailsFormData = {
  companyName: '',
  billingEmail: '',
  taxId: '',
  building: '',
  street1: '',
  street2: '',
  postalCode: '',
  region: '',
  city: '',
  countryCode: '',
};

const toFormValues = (details?: BillingDetails): BillingDetailsFormData => ({
  companyName: details?.companyName ?? '',
  billingEmail: details?.billingEmail ?? '',
  taxId: details?.taxId ?? '',
  building: details?.address?.building ?? '',
  street1: details?.address?.street1 ?? '',
  street2: details?.address?.street2 ?? '',
  postalCode: details?.address?.postalCode ?? '',
  region: details?.address?.region ?? '',
  city: details?.address?.city ?? '',
  countryCode: details?.address?.countryCode ?? '',
});

interface BillingDetailsFormProps {
  billingDetails?: BillingDetails;
  loading?: boolean;
  onSubmit: (values: BillingDetailsFormData) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  embedded?: boolean;
}

const requiredAsterisk = <span className="ml-1 text-red-600">*</span>;

const BillingDetailsForm = ({
  billingDetails,
  loading,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  embedded = false,
}: BillingDetailsFormProps) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<BillingDetailsFormData>({
    defaultValues: billingDetails ? toFormValues(billingDetails) : defaultValues,
  });

  useEffect(() => {
    reset(billingDetails ? toFormValues(billingDetails) : defaultValues);
  }, [billingDetails, reset]);

  const sectionClassName = embedded
    ? 'rounded-2xl border border-slate-200 bg-white p-6'
    : 'rounded-xl border border-slate-200 bg-white p-5';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        <section className={sectionClassName}>
          <h2 className="text-base font-semibold text-slate-900">Company Details</h2>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="lg:col-span-2">
              <label htmlFor="companyName" className="block font-medium text-gray-700">
                Company Name:{requiredAsterisk}
              </label>
              <Controller
                name="companyName"
                control={control}
                rules={{ required: 'Company name is required' }}
                render={({ field }) => (
                  <input
                    id="companyName"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label htmlFor="billingEmail" className="block font-medium text-gray-700">
                Billing Email:{requiredAsterisk}
              </label>
              <Controller
                name="billingEmail"
                control={control}
                rules={{
                  required: 'Billing email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                }}
                render={({ field }) => (
                  <input
                    id="billingEmail"
                    type="email"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
              {errors.billingEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.billingEmail.message}</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label htmlFor="taxId" className="block font-medium text-gray-700">
                Tax ID:
              </label>
              <Controller
                name="taxId"
                control={control}
                render={({ field }) => (
                  <input
                    id="taxId"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
            </div>
          </div>
        </section>

        <section className={sectionClassName}>
          <h2 className="text-base font-semibold text-slate-900">Billing Address</h2>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="lg:col-span-2">
              <label htmlFor="building" className="block font-medium text-gray-700">
                Building:
              </label>
              <Controller
                name="building"
                control={control}
                render={({ field }) => (
                  <input
                    id="building"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
            </div>

            <div className="lg:col-span-2">
              <label htmlFor="street1" className="block font-medium text-gray-700">
                Street 1:{requiredAsterisk}
              </label>
              <Controller
                name="street1"
                control={control}
                rules={{ required: 'Street 1 is required' }}
                render={({ field }) => (
                  <input
                    id="street1"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
              {errors.street1 && (
                <p className="mt-1 text-sm text-red-600">{errors.street1.message}</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label htmlFor="street2" className="block font-medium text-gray-700">
                Street 2:
              </label>
              <Controller
                name="street2"
                control={control}
                render={({ field }) => (
                  <input
                    id="street2"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
            </div>

            <div>
              <label htmlFor="postalCode" className="block font-medium text-gray-700">
                Postal Code:{requiredAsterisk}
              </label>
              <Controller
                name="postalCode"
                control={control}
                rules={{ required: 'Postal code is required' }}
                render={({ field }) => (
                  <input
                    id="postalCode"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
              {errors.postalCode && (
                <p className="mt-1 text-sm text-red-600">{errors.postalCode.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="region" className="block font-medium text-gray-700">
                Region:
              </label>
              <Controller
                name="region"
                control={control}
                render={({ field }) => (
                  <input
                    id="region"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
            </div>

            <div>
              <label htmlFor="city" className="block font-medium text-gray-700">
                City:{requiredAsterisk}
              </label>
              <Controller
                name="city"
                control={control}
                rules={{ required: 'City is required' }}
                render={({ field }) => (
                  <input
                    id="city"
                    type="text"
                    {...field}
                    className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                  />
                )}
              />
              {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
            </div>

            <div>
              <Controller
                name="countryCode"
                control={control}
                rules={{ required: 'Country code is required' }}
                render={({ field }) => (
                  <CountrySelectBox
                    value={field.value}
                    label={
                      <span className="block mb-1 font-medium text-gray-700">
                        Country:{requiredAsterisk}
                      </span>
                    }
                    onSelect={(country: CountryItem | null) => field.onChange(country?.id || '')}
                  />
                )}
              />
              {errors.countryCode && (
                <p className="mt-1 text-sm text-red-600">{errors.countryCode.message}</p>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={loading}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default BillingDetailsForm;
